/**
 * Damage code -> German observation wording.
 *
 * TWO PROVENANCES, and the difference matters:
 *
 * 1. **Authored** entries carry `authored: true`. Their wording is the product owner's, given as
 *    the canonical text for that code combination. This is the stronger source: it states what
 *    the code MEANS rather than what one vendor's van happened to print, so it is not subject to
 *    the sample counts below and it wins over any corpus reading.
 * 2. **Mined** entries are the wording that actually accompanied that combination in the OCR
 *    corpus, with `samples` and `agreement` recording how many detections agreed. Nothing here
 *    was invented: a combination with no authored text and too little corpus support is left out
 *    so it falls to {@link MAIN_CODE_WORDING} instead, which says only what the main code means.
 *
 * The counterpart of PHRASE_CODES in videoTextParser.ts, which goes the other way (text ->
 * code) for a reviewer transcribing an overlay by hand. The two are kept in step: every entry
 * here has a pattern there that reads its wording back to the same codes.
 */
export interface ObservationWording {
  /** The wording, without station, clock position or quantities. */
  text: string;
  /**
   * Quantity slots this code states, in Q1, Q2 order.
   *
   * An EMPTY label means the wording already names the measurement ("..., Querschnittsreduzierung
   * %q1%"), so the slot renders the bare value and unit. A label is only given when the text does
   * not name it and the van prints one ("Breite = 2mm").
   */
  quantities: Array<{ label: string; unit: string }>;
  /** Given as canonical wording rather than read off a corpus. See the file comment. */
  authored?: true;
  /**
   * The key this entry's wording parses BACK to, when it is not its own.
   *
   * Currently unused: the BAB matrix gave every combination distinct wording, so nothing shares.
   * Kept because two combinations being given the same words is a recurring hazard, and the
   * round-trip test reads this rather than skipping an entry it cannot resolve.
   *
   * Two combinations can be given the same words -- BAB|B|A and BAB|B|B both read "Riss in
   * LÃ¤ngsrichtung" -- and text alone then cannot tell them apart. Naming the winner here keeps
   * the round-trip test honest: it asserts the wording resolves to THIS combination, or to the
   * one that shares it, rather than quietly not being checked at all.
   */
  readsBackAs?: string;
  /** How many corpus detections back this entry, and how many agreed verbatim. */
  samples: number;
  agreement: number;
}

/** Keyed `MAIN|C1|C2`, exactly as {@link wordingKey} builds it. */
export const OBSERVATION_WORDING: Record<string, ObservationWording> = {
  "BAA|A|": {
    text: "Verformung – vertikal (Rohrhöhe verändert)",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|A|": {
    // C1 A/B/C is the crack itself, C2 its course: blank unspecified, A along the pipe, B around
  // it, C shard-shaped. The blank-C2 entries are what a run that codes no course gets.
    text: "Rissbildung – Oberflächenriss (Haarriss)",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|A|A": {
    text: "Rissbildung – Oberflächenriss, in Längsrichtung",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|A|B": {
    text: "Rissbildung – Oberflächenriss, am Rohrumfang",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|A|C": {
    text: "Rissbildung – Oberflächenriss, scherbenförmig",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|A|Y": {
    // C2 Y sits outside the A/B/C course matrix -- kept because the model emits it and it says
  // something the blank entry does not: the course was looked for and could not be read.
    text: "Rissbildung – Oberflächenriss (Haarriss), Verlauf unklar",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|B|": {
    text: "Rissbildung – Riss",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|B|A": {
    text: "Rissbildung – Riss, in Längsrichtung",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|B|B": {
    text: "Rissbildung – Riss, am Rohrumfang",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|B|C": {
    text: "Rissbildung – Riss, scherbenförmig",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|C|": {
    text: "Rissbildung – Klaffender Riss",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|C|A": {
    text: "Rissbildung – Klaffender Riss, in Längsrichtung",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|C|B": {
    text: "Rissbildung – Klaffender Riss, am Rohrumfang",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAB|C|C": {
    text: "Rissbildung – Klaffender Riss, scherbenförmig",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAC|A|": {
    text: "Rohrbruch/Einsturz – Bruch",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAF|B|": {
    // C2 (the cause) is still to be assigned; this becomes BAF|B|<C2> when it arrives.
    text: "Oberflächenschaden – Abplatzungen",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAG||": {
    text: "Einragender Anschluss",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAH|C|": {
    text: "Schadhafter Anschluss – Spalt am Anschlussumfang",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAJ|A|": {
    text: "Verschobene Verbindung – längs",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAJ|B|": {
    text: "Verschobene Verbindung – radial",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAJ|C|": {
    text: "Verschobene Verbindung – abgewinkelt",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BAO||": {
    text: "Boden sichtbar",
    quantities: [],
    samples: 3,
    agreement: 1.0,
  },
  "BBA|B|": {
    // The label lives in the SLOT, not the sentence: with nothing measured, label and value
  // disappear together instead of leaving a dangling "..., Querschnittsreduzierung".
    text: "Wurzeln – einzelne, feine Wurzeln, %q1%",
    quantities: [{ label: "Querschnittsreduzierung", unit: "" }],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BBA|C|": {
    text: "Wurzeln – komplexes Wurzelwerk, %q1%",
    quantities: [{ label: "Querschnittsreduzierung", unit: "" }],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BBB|A|": {
    text: "Anhaftende Stoffe – Inkrustation",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BCA|A|A": {
    text: "Anschluss – Abzweig mit vorgefertigtem Verbindungsstück, offen",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BCA|A|B": {
    text: "Anschluss, Abzweig, geschlossen, %q1%, %q2%",
    quantities: [{ label: "Höhe", unit: "mm" }, { label: "Breite", unit: "mm" }],
    samples: 18,
    agreement: 1.0,
  },
  "BCA|D|A": {
    text: "Anschluss – einfacher Anschluss, gebohrt, offen",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BCA|D|B": {
    text: "Anschluss – einfacher Anschluss, gebohrt, geschlossen",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BCA|E|A": {
    text: "Anschluss – ohne Formstück, gemeißelt, offen",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BCA|Z|A": {
    // "Andere Art" is only meaningful with a written annotation, so a row coded Z needs the SA
  // field filled in. The requirement is the reviewer's to meet; the wording states the code.
    text: "Anschluss – andere Art, offen",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BCA|Z|B": {
    text: "Anschluss – andere Art, geschlossen",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BCC||": {
    // C1 (the direction) is still to be assigned -- same as BAF|B above.
    text: "Krümmung der Leitung",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BCD|XP|": {
    text: "Anfangsknoten – Rohranfang",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
  "BCE|XP|": {
    text: "Endknoten – Rohrende",
    quantities: [],
    authored: true,
    samples: 0,
    agreement: 0,
  },
};

/**
 * Wording for a MAIN CODE on its own, used when the exact combination is unknown.
 *
 * The same association, one level coarser, and taken from the same place: PHRASE_CODES in
 * videoTextParser.ts already maps each of these phrases to its code for a reviewer transcribing
 * an overlay by hand, so the pairing is the corpus's, not mine.
 *
 * The rule that makes this safe: an entry here states ONLY what the main code means. Nothing
 * about a characterisation, because C1/C2 are exactly what is unknown at this tier -- BBB with
 * any characterisation is "Anhaftende Stoffe", and the letters themselves stay visible in the
 * MC / C1 / C2 columns beside the text. A code whose corpus wording is characterisation-specific
 * (BBA's "einzelne, feine Wurzeln") is deliberately NOT here: answering an unknown C1 with a
 * known C1's sentence is the one thing this file exists to prevent.
 *
 * Quantities are not declared at this tier either -- which quantity a code states depends on the
 * characterisation -- so a value the reviewer entered is appended by the caller instead.
 */
export const MAIN_CODE_WORDING: Record<string, ObservationWording> = {
  // Every main code PHRASE_CODES pins, and only what the main code itself means. Several of
  // these also have exact entries above with a characterisation spelled out ("Wurzeln, einzelne,
  // feine Wurzeln"); this tier answers the combinations those do not cover, and answers them
  // with the bare damage — never with the other characterisation's sentence.
  BAA: { text: "Verformung", quantities: [], samples: 0, agreement: 0 },
  BAB: { text: "Rissbildung, Riss", quantities: [], samples: 0, agreement: 0 },
  BAC: { text: "Rohrbruch/Einsturz", quantities: [], samples: 0, agreement: 0 },
  BAF: { text: "Oberflächenschaden", quantities: [], samples: 0, agreement: 0 },
  BAG: { text: "Einragender Anschluss", quantities: [], samples: 0, agreement: 0 },
  BAH: { text: "Schadhafter Anschluss", quantities: [], samples: 0, agreement: 0 },
  BAJ: { text: "Verschobene Verbindung", quantities: [], samples: 0, agreement: 0 },
  BBA: { text: "Wurzeln", quantities: [], samples: 0, agreement: 0 },
  BBB: { text: "Anhaftende Stoffe", quantities: [], samples: 0, agreement: 0 },
  BBC: { text: "Ablagerungen", quantities: [], samples: 0, agreement: 0 },
  BCA: { text: "Anschluss", quantities: [], samples: 0, agreement: 0 },
  BCB: { text: "Punktuelle Reparatur", quantities: [], samples: 0, agreement: 0 },
  BCC: { text: "Krümmung der Leitung", quantities: [], samples: 0, agreement: 0 },
  BCD: { text: "Rohranfang", quantities: [], samples: 0, agreement: 0 },
  BCE: { text: "Rohrende", quantities: [], samples: 0, agreement: 0 },
  BDD: { text: "Wasserspiegel", quantities: [], samples: 0, agreement: 0 },
};

/**
 * The lookup key for a coded observation.
 *
 * Matching is EXACT on all three codes, with no fall back to a shorter key: the wording
 * spells the characterisation out ("einzelne, feine Wurzeln"), so answering a C1 the table
 * does not know with a different C1's sentence would state a characterisation the model
 * never reported.
 */
export const wordingKey = (mainCode?: string, c1?: string, c2?: string): string =>
  `${(mainCode ?? "").trim()}|${(c1 ?? "").trim()}|${(c2 ?? "").trim()}`;

/**
 * The wording for a coded observation: the exact combination when the corpus pins one down,
 * otherwise what the main code alone means.
 *
 * Two tiers rather than a prefix match. The exact entries spell characterisations out and must
 * only ever answer their own combination; the main-code tier says nothing a C1 could contradict,
 * so it is safe to answer any characterisation with. A code in neither renders through the plain
 * sentence, which describes the row truthfully rather than describing a different damage.
 */
export const wordingFor = (
  mainCode?: string,
  c1?: string,
  c2?: string
): ObservationWording | undefined =>
  OBSERVATION_WORDING[wordingKey(mainCode, c1, c2)] ??
  MAIN_CODE_WORDING[(mainCode ?? "").trim().toUpperCase()];
